import auth0 from "auth0-js";
import axios from "axios";

export const AUTHORIZATION_HEADER_KEY = "Authorization";

export type AuthServiceType = {
  isAuthenticated: () => boolean;
  handleAuthentication: () => Promise<string | any>;
  scheduleRenewal: () => void;
  renewToken: () => void;
  logout: (callback?: () => void) => void;
  signInWithPhone: () => void;
  signInWithEmail: () => Promise<void>;
};
class AuthService {
  tokenRenewalTimeout: any;

  constructor() {
    this.scheduleRenewal();

    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      axios.defaults.headers.common[AUTHORIZATION_HEADER_KEY] =
        `Bearer ${accessToken}`;
    }
  }

  auth0 = new auth0.WebAuth({
    domain: import.meta.env.VITE_AUTH0_DOMAIN as string,
    clientID: import.meta.env.VITE_AUTH0_CLIENT_ID as string,
    redirectUri: `${window.location.origin}/callback`,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE as string,
    responseType: "token id_token",
    scope:
      "openid create:keyrequests create:invites onboard:objects onboard:screens",
  });
  isAuthenticated = () => {
    // Check whether the current time is past the
    // Access Token's expiry time
    const expiresInString = localStorage.getItem("expires_in");
    if (!expiresInString) {
      return false;
    }

    const expiresAt = JSON.parse(expiresInString);

    return new Date().getTime() < expiresAt;
  };

  handleAuthentication(): Promise<string | any> {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash(async (err, authResult) => {
        if (err) {
          return reject(err);
        }

        if (authResult && authResult.accessToken && authResult.idToken) {
          localStorage.setItem("access_token", authResult.accessToken);
          axios.defaults.headers.common[AUTHORIZATION_HEADER_KEY] =
            `Bearer ${authResult.accessToken}`;

          if (authResult.expiresIn) {
            const expiresAt = JSON.stringify(
              authResult.expiresIn * 1000 + new Date().getTime(),
            );
            localStorage.setItem("expires_in", expiresAt);
            this.scheduleRenewal();
          }

          resolve(true);
        }
      });
    });
  }

  scheduleRenewal() {
    const expiresInString = localStorage.getItem("expires_in");

    if (expiresInString) {
      const expiresAt = JSON.parse(expiresInString);

      const delay = expiresAt - Date.now();

      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken();
      }, delay);
    }
  }

  renewToken() {
    this.auth0.checkSession({}, (err, authResult) => {
      if (err) {
        this.logout();
      } else {
        localStorage.setItem("access_token", authResult.accessToken);
        axios.defaults.headers.common[AUTHORIZATION_HEADER_KEY] =
          `Bearer ${authResult.accessToken}`;

        if (authResult.expiresIn) {
          const expiresAt = JSON.stringify(
            authResult.expires_in * 1000 + new Date().getTime(),
          );
          localStorage.setItem("expires_in", expiresAt);
        }
      }
    });
  }

  logout = (callback?: () => void) => {
    localStorage.clear();
    axios.defaults.headers.common[AUTHORIZATION_HEADER_KEY] = undefined;
    this.auth0.logout({ returnTo: window.location.origin });
    if (callback) {
      callback();
    }
  };

  signInWithPhone = () => {
    // the custom Universal login template in Auth0 tenant causes this to work as sms passwordless
    this.auth0.authorize();
  };

  signInWithEmail = async () => {
    // the customer Universal login template requires this connection, otherwise sms passwordless will be used

    const data = await this.auth0.authorize({
      connection: "Username-Password-Authentication",
    });
    console.log("logged-in", data);
  };
}

const auth: AuthServiceType = new AuthService();

export default auth;
